'use client';
import ReactDOM from 'react-dom';

import Button from '@/components/atoms/button';
import { Input } from '@/components/atoms/input';
import { gatedContentRootId } from '@/components/atoms/layout/CMSBlogLayout';
import RichTextFieldSB from '@/components/atoms/richTextField/RichTextFieldSB';

import { GatedContentProps } from './types';
import useCaptureUserDetails from './useCaptureUserDetails';
import useGatedContentStyleHack from './useGatedContentStyleHack';

const GatedContent = (props: GatedContentProps) => {
  const { errors, handleSubmit, values, getFieldProps, isLoading, gateId } =
    useCaptureUserDetails(props);

  const { hasApplied } = useGatedContentStyleHack({ gateId });

  // until the check for gate clearance has been made, render an empty element that
  // serves as a wall when scanning through elements to find children that need to be hidden
  if (!hasApplied) return <div id={gateId} />;

  // once the check is done, render the content in a portal targeting the root container
  const Content = (
    <div className='relative z-40 w-full' id={gateId}>
      <div className='to-neutral-N00 -mt-24 h-24 bg-gradient-to-b from-transparent' />
      <div className='light dark:bg-neutral-N00'>
        <section
          className='dark:bg-neutral-N00 dark:text-neutral-N99 h-min w-full justify-center px-5 py-8 md:mx-auto md:max-w-5xl md:px-10 md:py-12 lg:px-[80px] lg:text-center'
          id={gateId}
        >
          <div className=' mb-6'>
            <RichTextFieldSB block={props.title} />
          </div>
          <div className=''>
            <RichTextFieldSB block={props.description} />
          </div>
          <div className='mx-auto lg:max-w-[400px]'>
            <form className='flex flex-col' onSubmit={handleSubmit}>
              <Input
                placeholder='Type here'
                label='Email address*'
                required
                error={errors.email || ''}
                {...getFieldProps('email')}
              />
              <Button
                classNames={{
                  root: 'w-full mb-6',
                }}
                disabled={!values.email || isLoading}
              >
                <RichTextFieldSB block={props.submitButtonText} />
              </Button>
            </form>
            <div className='text-body-s text-neutral-N50'>
              <RichTextFieldSB block={props.termsAndConditions} />
            </div>
          </div>
        </section>
      </div>
    </div>
  );

  const gatedContentContainer = document.querySelector(
    `#${gatedContentRootId}`
  );

  if (!gatedContentContainer) {
    console.error(
      'Gated content container not found for gateId:',
      gateId,
      '. Skipping.'
    );
    return null;
  }

  return ReactDOM.createPortal(Content, gatedContentContainer);
};

export default GatedContent;
